import { readPref, savePref } from './prefsService'
import type { CanvasEdge, CanvasNode } from './canvasService'

/**
 * A person's workflows in the board's toolbar (Huanrui, 2026-10-16)
 * -------------------------------------------------------------
 * Kept with their account (user_prefs 'workflows', docs/workflows.sql) and
 * in this browser for the first paint. Each is a COPY: the steps as they were
 * when added, and the project files they use copied into the person's hidden
 * personal project (`project`). Changing a workflow on a board never changes
 * the toolbar's; "Update in toolbar" on the card does, on purpose.
 *
 * Not shared with anybody: a colleague gets a workflow by being given the
 * project it is on, and adds it to their own toolbar from there.
 */
export interface ToolbarWorkflow {
  id: string
  name: string
  glyph: string
  savedAt: string
  /** The workflow card's own data (workflowNode.tsx WorkflowDef), as JSON. */
  def: Record<string, unknown>
  /** The workflow's page: its steps and pictures, from its top left corner. */
  doc: { nodes: CanvasNode[]; edges: CanvasEdge[] }
  /** Where its project files are kept: this person's personal project. */
  project: string
}

const KEY = 'ww-toolbar-workflows'
let list: ToolbarWorkflow[] = cached()
const listeners = new Set<() => void>()

function cached(): ToolbarWorkflow[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? '[]') as unknown
    return Array.isArray(raw) ? (raw as ToolbarWorkflow[]) : []
  } catch {
    return []
  }
}

function emit() {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list))
  } catch {
    /* only the browser's copy */
  }
  for (const listener of listeners) listener()
}

/** Read once from the account; the browser's copy stands until it answers. */
let read = false
export function loadToolbarWorkflows(): void {
  if (read) return
  read = true
  void readPref('workflows')
    .then((value) => {
      if (Array.isArray(value)) {
        list = value as ToolbarWorkflow[]
        emit()
      }
    })
    .catch(() => {
      read = false
    })
}

export function toolbarWorkflows(): ToolbarWorkflow[] {
  return list
}

export function onToolbarWorkflows(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function keep() {
  emit()
  savePref('workflows', list, 0)
}

/** Add, or replace the one with the same id. */
export function putToolbarWorkflow(entry: ToolbarWorkflow): void {
  list = [...list.filter((one) => one.id !== entry.id), entry]
  keep()
}

export function removeToolbarWorkflow(id: string): void {
  list = list.filter((one) => one.id !== id)
  keep()
}
